import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import {
  Button,
  Card,
  Dialog,
  DropdownInput,
  EmptyState,
  Form,
  Icon,
  useForm,
  VariableTextInput,
} from "@validereinc/common-components";
import { FormSubmissionTaskType } from "@validereinc/domain";
import { produce } from "immer";
import React, { useEffect, useMemo, useState } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";

type FormValuesType = {
  inputName: string;
  inputValue: string;
};

const VariableCard = ({
  variableName,
  variableValue,
  formSchemaId,
  removeVariable,
}: {
  variableName: string;
  variableValue: string;
  formSchemaId: string;
  removeVariable: (variableName: string) => void;
}) => {
  const formSchemaQuery = useGetOneFormSchema(
    {
      id: formSchemaId,
    },
    { enabled: !!formSchemaId }
  );

  const cleanName = useMemo(() => {
    if (variableName.startsWith("$.questions.")) {
      const qId = variableName
        .replace("$.questions.", "")
        .replace(".default_answer", "");

      if (!formSchemaQuery.data?.data?.config.questions?.[qId]?.prompt) {
        return "";
      }

      return `Question ${formSchemaQuery.data?.data?.config.questions?.[qId]?.prompt}`;
    }

    return variableName;
  }, [variableName, JSON.stringify(formSchemaQuery.data?.data)]);

  if (!cleanName) return null;

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <strong>{cleanName}</strong> has default value of{" "}
          <strong>{variableValue}</strong>
        </div>
        <Button
          icon="minus-circle"
          variant="error-outline"
          style={{ width: "fit-content" }}
          onClick={() => {
            removeVariable(variableName);
          }}
        />
      </div>
    </Card>
  );
};

const WorkflowConfigurationInputVariableForm = ({
  stepDefinition,
  selectedNodeId,
}: {
  stepDefinition: FormSubmissionTaskType;
  selectedNodeId?: string;
}) => {
  const formSchemaQuery = useGetOneFormSchema(
    {
      id: stepDefinition.form_schema_id,
    },
    { enabled: !!stepDefinition.form_schema_id }
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const form = useForm<FormValuesType>();
  const [localVariables, setLocalVariables] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    setLocalVariables(stepDefinition.input ?? {});
  }, [JSON.stringify(stepDefinition.input)]);

  const { getAvailableStepVariables } = useWorkflowGraphState();
  const { setInput } = useWorkflowConfigurationFormStore();

  useEffect(() => {
    if (isDialogOpen) form.reset();
  }, [isDialogOpen]);

  useEffect(() => {
    setInput(localVariables);
  }, [JSON.stringify(localVariables)]);

  const handleSubmit = (data: FormValuesType) => {
    if (!selectedNodeId) return;
    setLocalVariables((currentValue) =>
      produce(currentValue, (draft) => {
        draft[data.inputName] = data.inputValue;
      })
    );
    setIsDialogOpen(false);
  };

  const formSchema = formSchemaQuery.data?.data;

  const questionsList = Object.entries(formSchema?.config.questions ?? {}).map(
    (q) => ({
      value: `$.questions.${q[0]}.default_answer`,
      label: q[1]?.prompt ?? "",
    })
  );

  const variables = Object.entries(localVariables);

  const availableVariables = getAvailableStepVariables(selectedNodeId ?? "");

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {variables.length === 0 && (
          <EmptyState
            title="No pre-filled answers configured"
            suggestion="Configure pre-filled answers to selected questions."
            icon={<Icon variant="files" />}
            style={{ padding: 16 }}
          />
        )}

        {variables.map((variable) => (
          <VariableCard
            key={variable[0]}
            variableName={variable[0]}
            variableValue={variable[1]}
            formSchemaId={stepDefinition.form_schema_id}
            removeVariable={(variableName) => {
              setLocalVariables((currentValue) =>
                produce(currentValue, (draft) => {
                  delete draft[variableName];
                })
              );
            }}
          />
        ))}

        <Button
          icon="plus-circle"
          style={{ width: "100%" }}
          onClick={() => setIsDialogOpen(true)}
        >
          Assign Input Variables
        </Button>
      </div>

      <Dialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        title="Assign Input Variable"
        actionRow={[
          <Button
            key="create"
            variant="primary"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={form.handleSubmit(handleSubmit)}
            disabled={!form.formState.isValid}
          >
            Create
          </Button>,
        ]}
      >
        <Form {...form}>
          <DropdownInput
            name="inputName"
            label="Reference Question"
            options={questionsList}
            labelKey="label"
            valueKey="value"
          />

          <VariableTextInput
            label="Value"
            name="inputValue"
            isRequired
            rules={{
              validate: {
                validateExistingVariable: (value: string) =>
                  Object.keys(localVariables ?? {}).includes(value)
                    ? "This variable already exists for this step."
                    : true,
              },
            }}
            suggestions={availableVariables}
          />
        </Form>
      </Dialog>
    </>
  );
};

export default WorkflowConfigurationInputVariableForm;
